import React from 'react';
import { Descriptions } from 'antd';
import { DocumentType } from '@nxsys/types/models/document';
import { formatStatus } from '@nxsys/utils';
import { IconDownload, IconCheck } from '@nxsys/components/AppIconSvg';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import AppAttachment from '@nxsys/components/AppAttachment';
import AppDescription from '@nxsys/components/AppDescription';
import AppModalContainer from '@nxsys/components/AppModalContainer';

type DetailOnboardingModalProps = {
  isOpen: boolean;
  title: string | React.ReactNode;
  documentSelected: DocumentType | null;
  toggle: () => void;
  onVerifyDocument: (document: DocumentType) => void;
  hideVerify?: boolean;
};

const DetailOnboardingModal: React.FC<DetailOnboardingModalProps> = ({
  title,
  isOpen,
  documentSelected,
  toggle,
  onVerifyDocument,
  hideVerify = false,
}: DetailOnboardingModalProps) => {
  const onVerify = () => {
    if (documentSelected) onVerifyDocument(documentSelected);
  };
  const subcontractorRef = documentSelected?.subcontractorRef;
  const prospectsOnboardingRef = documentSelected?.prospectsOnboardingRef;
  const author = subcontractorRef || prospectsOnboardingRef;
  const agencyRef =
    documentSelected?.agencyRef ||
    documentSelected?.prospectsOnboardingRef?.agency;
  return (
    <AppModalContainer
      title={
        <>
          {title}
          <AppTagStatus
            className={`tag-popup ${formatStatus(documentSelected?.documentStatus || '')}`}
          >
            {formatStatus(documentSelected?.documentStatus || '_')}
          </AppTagStatus>
        </>
      }
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: 'Download',
          variant: 'outlined',
          buttonType: 'primary',
          icon: <IconDownload width={20} height={20} color="#00795B" />,
          onClick: () => {
            if (documentSelected?.documentFile) {
              window.open(documentSelected.documentFile, '_blank');
            }
          },
        },
        ...((!hideVerify
          ? [
              {
                name: 'Verify',
                buttonType: 'primary',
                icon: <IconCheck width={20} height={20} />,
                onClick: onVerify,
              },
            ]
          : []) as any),
      ]}
    >
      <AppDescription
        column={1}
        labelStyle={{ width: 216 }}
        bordered={false}
        colon={false}
        size="middle"
      >
        <Descriptions.Item label="Doc ID">
          {documentSelected?.id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Document Title">
          {documentSelected?.documentTitle || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Subcontrator & Employee">
          {author ? `${author?.forename} ${author?.forename}` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Agency Name">
          {agencyRef?.name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Share Code / EC Check Code">
          {documentSelected?.documentShareCode || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Attachment">
          {documentSelected?.documentFile ? (
            <a
              href={documentSelected.documentFile}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <AppAttachment
                urlFile={documentSelected.documentFile}
                title={documentSelected.documentName}
                className="attachment-box"
              />
            </a>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Created By">
          {author ? `${author?.forename} ${author?.forename}` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Completed By">
          {prospectsOnboardingRef
            ? `${prospectsOnboardingRef?.forename} ${prospectsOnboardingRef?.forename}`
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Completed On">
          {subcontractorRef?.createdAt
            ? new Date(subcontractorRef?.createdAt).toLocaleDateString()
            : '-'}
        </Descriptions.Item>
      </AppDescription>
    </AppModalContainer>
  );
};

export default DetailOnboardingModal;
